import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ElementRef,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AppService } from 'src/app/services/app-service.service';
import { Buffer } from 'buffer';

export type Message = {
  sender: string;
  message: string;
  typ: 'text' | 'audio';
};
@Component({
  selector: 'app-chat-bot',
  templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss'],
})
export class ChatBotComponent implements OnInit, OnDestroy {
  isShowChat = false;
  message: any = '';
  messageList: Message[] = [];
  internalMessageList: any = [];
  showBotLoader = false;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  isRecording = false;
  isAiSpeaking = false;
  private audioElement: HTMLAudioElement | null = null;
  randomThinkingWord: string = 'Thinking';
  private thinkingWords: string[] = [
    'Reasoning',
    'Evaluating',
    'Processing',
    'Analyzing',
    'Interpreting',
    'Scrutinizing',
    'Examining',
    'Assessing',
    'Deliberating',
    'Reflecting',
    'Contemplating',
    'Inferring',
    'Synthesizing',
  ];
  private lastThinkingWordIndex: number = -1;
  private thinkingInterval: any = null;
  isTranscribing = false;

  constructor(
    private appService: AppService,
    private toastrService: ToastrService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    // Initialization code
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    // Check if chat is open and the click is outside the chat box
    if (
      this.isShowChat &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.closeChat();
    }
  }

  openChat(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isShowChat = true;
    this.appService.getChatList().subscribe((res: any) => {
      this.internalMessageList = [...res];
      this.messageList = [...res];
      setTimeout(() => {
        document.getElementsByClassName('body')[0].scrollTop =
          document.getElementsByClassName('body')[0].scrollHeight;
      }, 10);
    });
  }

  closeChat() {
    this.isShowChat = false;
  }

  onKeyDown(event: any) {
    if (event.code == 'Enter') {
      this.sendMessage();
    }
  }

  sendMessage() {
    this.updateMessageList(this.message, sessionStorage.getItem('userName'));

    this.message = '';
    this.showBotLoader = true;
    // Start cycling through thinking words
    this.startThinkingAnimation();

    setTimeout(() => {
      document.getElementsByClassName('body')[0].scrollTop =
        document.getElementsByClassName('body')[0].scrollHeight;
    }, 10);

    this.appService
      .postChatList(this.internalMessageList)
      .subscribe((res: any) => {
        this.updateMessageList(res, 'AiAssistant');

        setTimeout(() => {
          this.showBotLoader = false;
          // Stop the thinking animation when response is received
          this.stopThinkingAnimation();
          document.getElementsByClassName('body')[0].scrollTop =
            document.getElementsByClassName('body')[0].scrollHeight;
        }, 10);
      });
  }

  async startRecording() {
    try {
      if (!this.isRecording) {
        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
          this.handleAudioRecording(audioBlob);
        };

        this.mediaRecorder.start();
        this.isRecording = true;
      } else {
        this.stopRecording();
      }
    } catch (error) {
      // console.error('Error accessing microphone:', error);
      this.toastrService.error('Error', 'Error accessing microphone');
      // Handle error - show message to user
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      // Stop all audio tracks
      this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    }
    // Show the transcribing indicator before processing the audio
    this.showBotLoader = true;
    this.startThinkingAnimation(true);
  }

  private handleAudioRecording(audioBlob: Blob) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Audio = reader.result?.toString().split(',')[1];
      if (base64Audio) {
        this.updateMessageList(
          base64Audio,
          sessionStorage.getItem('userName'),
          true,
          true
        );
        console.log(this.internalMessageList);
        
        this.appService.postAudioChat(this.internalMessageList).subscribe({
          next: (response: any) => {
            console.log('response', response);
            if (response.typ === 'text') {
              this.updateMessageList(response.message, response.sender);
              this.startThinkingAnimation(false);
            } else {
              const audioUrl = this.b64ToUrl(response.message);
              this.updateMessageList(
                response.message,
                response.sender,
                true,
                true
              );
              this.playAudio(audioUrl);
            }
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Error in postAudioChat:', err);
          },
          complete: () => {
            this.showBotLoader = false;
            // Stop the thinking animation when response is complete
            this.stopThinkingAnimation();
          },
        });
      }
    };
    reader.readAsDataURL(audioBlob);
  }

  private b64ToUrl(b64Data: string) {
    const buffer = Buffer.from(b64Data, 'base64');
    // const byteNumbers = new Array(byteCharacters.length);
    // for (let i = 0; i < byteCharacters.length; i++) {
    //   byteNumbers[i] = byteCharacters[i];
    // }
    const byteArray = new Uint8Array(buffer);
    const blob = new Blob([byteArray], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  }

  updateMessageList(
    message: string,
    sender: string,
    isInternal: boolean = false,
    isAudio: boolean = false
  ) {
    const messageData = {
      sender: sender,
      message: message,
      typ: isAudio ? 'audio' : 'text',
    } as Message;
    if (isInternal) {
      this.internalMessageList.push(messageData);
    } else {
      this.messageList.push(messageData);
      this.internalMessageList.push(messageData);
    }
  }

  // New method to play audio with speaking state tracking
  private playAudio(audioUrl: string) {
    // Stop any currently playing audio first
    this.stopSpeaking();

    // Create and play new audio
    this.audioElement = new Audio(audioUrl);
    this.isAiSpeaking = true;

    this.audioElement.onended = () => {
      this.isAiSpeaking = false;
      this.audioElement = null;
      this.cdr.detectChanges();
    };

    this.audioElement.play().catch((error) => {
      console.error('Error playing audio:', error);
      this.isAiSpeaking = false;
      this.cdr.detectChanges();
    });

    this.showBotLoader = false;
  }

  // New method to stop AI speaking
  stopSpeaking() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement = null;
    }
    this.isAiSpeaking = false;
  }

  // Update this method to avoid repetition
  private getRandomThinkingWord(): string {
    let randomIndex: number;
    // Keep generating a new random index until it's different from the last one
    do {
      randomIndex = Math.floor(Math.random() * this.thinkingWords.length);
    } while (
      randomIndex === this.lastThinkingWordIndex &&
      this.thinkingWords.length > 1
    );

    // Save the current index for next comparison
    this.lastThinkingWordIndex = randomIndex;
    return this.thinkingWords[randomIndex];
  }

  private startThinkingAnimation(isAudio: boolean = false) {
    this.stopThinkingAnimation();
    
    if (isAudio) {
      this.isTranscribing = true;
      this.randomThinkingWord = 'Transcribing';
    } else {
      this.isTranscribing = false;
      this.randomThinkingWord = 'Thinking';
      setTimeout(() => {
        this.randomThinkingWord = this.getRandomThinkingWord();
        this.thinkingInterval = setInterval(() => {
          this.randomThinkingWord = this.getRandomThinkingWord();
          this.cdr.detectChanges();
        }, 10000);
      }, 5000);
    }
  }

  private stopThinkingAnimation() {
    if (this.thinkingInterval) {
      clearInterval(this.thinkingInterval);
      this.thinkingInterval = null;
    }
    this.isTranscribing = false;
  }

  ngOnDestroy() {
    this.stopThinkingAnimation();
  }
}
